export type ThemeCategory = 'all' | 'simple' | 'business' | 'cool' | 'cute' | 'natural';

export interface ThemeClasses {
  pageBg: string;
  pageContainer: string;
  cardBg: string;
  cardBorder: string;
  headerCardBg: string;
  accentBadge: string;
  primaryBtn: string;
  primaryText: string;
  titleText: string;
  bodyText: string;
  mutedText: string;
  divider: string;
  selectedOption: string;
  optionCard: string;
  tableHeader: string;
  tableRowEven: string;
  activeTab: string;
  inactiveTab: string;
}

export interface ThemeDefinition {
  id: string;
  nameKey: string;
  fallbackName: string;
  category: Exclude<ThemeCategory, 'all'>;
  descriptionKey: string;
  fallbackDescription: string;
  emoji: string;
  previewColors: [string, string, string];
  isDark: boolean;
  classes: ThemeClasses;
}

export const THEME_CATEGORIES: { id: ThemeCategory; labelKey: string; fallbackLabel: string; emoji: string }[] = [
  { id: 'all', labelKey: 'theme.catAll', fallbackLabel: 'すべて', emoji: '🎨' },
  { id: 'simple', labelKey: 'theme.catSimple', fallbackLabel: 'シンプル', emoji: '✨' },
  { id: 'business', labelKey: 'theme.catBusiness', fallbackLabel: 'ビジネス', emoji: '💼' },
  { id: 'cool', labelKey: 'theme.catCool', fallbackLabel: 'クール＆ダーク', emoji: '⚡' },
  { id: 'cute', labelKey: 'theme.catCute', fallbackLabel: 'かわいい＆ポップ', emoji: '🌸' },
  { id: 'natural', labelKey: 'theme.catNatural', fallbackLabel: 'ナチュラル＆和風', emoji: '🌿' },
];

export const THEMES: Record<string, ThemeDefinition> = {
  // --- 1. シンプル / スタンダード (Simple & Standard) ---
  'default': {
    id: 'default',
    nameKey: 'theme.default.name',
    fallbackName: 'クラシック・インディゴ',
    category: 'simple',
    descriptionKey: 'theme.default.desc',
    fallbackDescription: '知性と思慮深さを感じさせる洗練された標準デザイン',
    emoji: '🗳️',
    previewColors: ['#4f46e5', '#818cf8', '#f8fafc'],
    isDark: false,
    classes: {
      pageBg: 'bg-slate-50 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white border-slate-200 shadow-sm text-slate-800',
      cardBorder: 'border-slate-200',
      headerCardBg: 'bg-white border-slate-200 text-slate-900',
      accentBadge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      primaryBtn: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-200',
      primaryText: 'text-indigo-600',
      titleText: 'text-slate-900',
      bodyText: 'text-slate-700',
      mutedText: 'text-slate-400',
      divider: 'border-slate-100',
      selectedOption: 'border-indigo-600 bg-indigo-50/70 shadow-indigo-100 text-indigo-950',
      optionCard: 'bg-white border-slate-200 hover:border-indigo-300 hover:bg-slate-50/80 text-slate-800',
      tableHeader: 'bg-slate-50 text-slate-700',
      tableRowEven: 'bg-slate-50/50',
      activeTab: 'bg-white text-indigo-600 shadow-xs',
      inactiveTab: 'text-slate-600 hover:text-slate-900',
    },
  },
  'minimal-slate': {
    id: 'minimal-slate',
    nameKey: 'theme.minimalSlate.name',
    fallbackName: 'ミニマル・スレート',
    category: 'simple',
    descriptionKey: 'theme.minimalSlate.desc',
    fallbackDescription: '白黒と無彩色グレーで整えた極限のミニマリズム',
    emoji: '⚪',
    previewColors: ['#18181b', '#71717a', '#f4f4f5'],
    isDark: false,
    classes: {
      pageBg: 'bg-zinc-100 text-zinc-900',
      pageContainer: 'text-zinc-900',
      cardBg: 'bg-white border-zinc-200 shadow-xs text-zinc-900',
      cardBorder: 'border-zinc-200',
      headerCardBg: 'bg-white border-zinc-300 text-zinc-900',
      accentBadge: 'bg-zinc-100 text-zinc-800 border-zinc-300',
      primaryBtn: 'bg-zinc-900 hover:bg-zinc-800 text-white shadow-zinc-200',
      primaryText: 'text-zinc-900',
      titleText: 'text-zinc-950',
      bodyText: 'text-zinc-700',
      mutedText: 'text-zinc-400',
      divider: 'border-zinc-100',
      selectedOption: 'border-zinc-900 bg-zinc-100 shadow-zinc-100 text-zinc-950',
      optionCard: 'bg-white border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 text-zinc-800',
      tableHeader: 'bg-zinc-100 text-zinc-800',
      tableRowEven: 'bg-zinc-50/60',
      activeTab: 'bg-zinc-900 text-white shadow-xs',
      inactiveTab: 'text-zinc-600 hover:text-zinc-900',
    },
  },
  'nordic-frost': {
    id: 'nordic-frost',
    nameKey: 'theme.nordicFrost.name',
    fallbackName: '北欧フロスト',
    category: 'simple',
    descriptionKey: 'theme.nordicFrost.desc',
    fallbackDescription: '澄んだアイスブルーと冷涼な空気を感じる北欧デザイン',
    emoji: '❄️',
    previewColors: ['#0284c7', '#38bdf8', '#f0f9ff'],
    isDark: false,
    classes: {
      pageBg: 'bg-sky-50/70 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white/90 backdrop-blur-xs border-sky-200 shadow-sm text-slate-800',
      cardBorder: 'border-sky-200',
      headerCardBg: 'bg-white/95 border-sky-200 text-slate-900',
      accentBadge: 'bg-sky-100 text-sky-800 border-sky-200',
      primaryBtn: 'bg-sky-600 hover:bg-sky-700 text-white shadow-sky-200',
      primaryText: 'text-sky-600',
      titleText: 'text-slate-900',
      bodyText: 'text-slate-700',
      mutedText: 'text-slate-400',
      divider: 'border-sky-100',
      selectedOption: 'border-sky-600 bg-sky-50/90 shadow-sky-100 text-sky-950',
      optionCard: 'bg-white/80 border-sky-100 hover:border-sky-300 hover:bg-sky-50/50 text-slate-800',
      tableHeader: 'bg-sky-100/60 text-sky-900',
      tableRowEven: 'bg-sky-50/40',
      activeTab: 'bg-white text-sky-700 shadow-xs',
      inactiveTab: 'text-slate-600 hover:text-sky-900',
    },
  },
  'warm-latte': {
    id: 'warm-latte',
    nameKey: 'theme.warmLatte.name',
    fallbackName: 'ウォーム・ラテ',
    category: 'simple',
    descriptionKey: 'theme.warmLatte.desc',
    fallbackDescription: '優しいミルクティーとサンドベージュの穏やかなトーン',
    emoji: '🥛',
    previewColors: ['#d97706', '#fde68a', '#fffbeb'],
    isDark: false,
    classes: {
      pageBg: 'bg-amber-50/50 text-stone-800',
      pageContainer: 'text-stone-800',
      cardBg: 'bg-white border-amber-200/80 shadow-sm text-stone-800',
      cardBorder: 'border-amber-200/80',
      headerCardBg: 'bg-white border-amber-200 text-stone-900',
      accentBadge: 'bg-amber-100 text-amber-900 border-amber-200',
      primaryBtn: 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-200',
      primaryText: 'text-amber-700',
      titleText: 'text-stone-900',
      bodyText: 'text-stone-700',
      mutedText: 'text-stone-400',
      divider: 'border-amber-100',
      selectedOption: 'border-amber-600 bg-amber-50 shadow-amber-100 text-amber-950',
      optionCard: 'bg-white border-stone-200 hover:border-amber-300 hover:bg-amber-50/30 text-stone-800',
      tableHeader: 'bg-amber-100/60 text-amber-950',
      tableRowEven: 'bg-amber-50/40',
      activeTab: 'bg-white text-amber-800 shadow-xs',
      inactiveTab: 'text-stone-600 hover:text-stone-900',
    },
  },

  // --- 2. ビジネス / フォーマル (Business & Formal) ---
  'corporate-navy': {
    id: 'corporate-navy',
    nameKey: 'theme.corporateNavy.name',
    fallbackName: 'コーポレート・ネイビー',
    category: 'business',
    descriptionKey: 'theme.corporateNavy.desc',
    fallbackDescription: '高い信頼と誠実さを醸し出す濃紺のビジネススタイル',
    emoji: '🏢',
    previewColors: ['#1e3a8a', '#3b82f6', '#f8fafc'],
    isDark: false,
    classes: {
      pageBg: 'bg-slate-100/80 text-slate-900',
      pageContainer: 'text-slate-900',
      cardBg: 'bg-white border-slate-300/80 shadow-sm text-slate-900',
      cardBorder: 'border-slate-300',
      headerCardBg: 'bg-slate-900 text-white border-slate-800',
      accentBadge: 'bg-blue-100 text-blue-900 border-blue-300 font-semibold',
      primaryBtn: 'bg-blue-900 hover:bg-blue-800 text-white shadow-blue-950/20',
      primaryText: 'text-blue-900',
      titleText: 'text-slate-950 font-black',
      bodyText: 'text-slate-800',
      mutedText: 'text-slate-500',
      divider: 'border-slate-200',
      selectedOption: 'border-blue-900 bg-blue-50/90 shadow-blue-100 text-blue-950',
      optionCard: 'bg-white border-slate-300 hover:border-blue-700 hover:bg-blue-50/40 text-slate-900',
      tableHeader: 'bg-slate-100 text-slate-900 font-bold',
      tableRowEven: 'bg-slate-50',
      activeTab: 'bg-blue-900 text-white shadow-xs',
      inactiveTab: 'text-slate-600 hover:text-slate-950',
    },
  },
  'executive-emerald': {
    id: 'executive-emerald',
    nameKey: 'theme.executiveEmerald.name',
    fallbackName: 'エグゼクティブ・エメラルド',
    category: 'business',
    descriptionKey: 'theme.executiveEmerald.desc',
    fallbackDescription: '深みのあるエメラルドグリーンと上品な佇まい',
    emoji: '💼',
    previewColors: ['#065f46', '#10b981', '#f0fdf4'],
    isDark: false,
    classes: {
      pageBg: 'bg-emerald-50/40 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white border-emerald-200 shadow-sm text-slate-800',
      cardBorder: 'border-emerald-200',
      headerCardBg: 'bg-white border-emerald-300 text-emerald-950',
      accentBadge: 'bg-emerald-100 text-emerald-900 border-emerald-300',
      primaryBtn: 'bg-emerald-700 hover:bg-emerald-800 text-white shadow-emerald-200',
      primaryText: 'text-emerald-700',
      titleText: 'text-emerald-950',
      bodyText: 'text-slate-700',
      mutedText: 'text-slate-400',
      divider: 'border-emerald-100',
      selectedOption: 'border-emerald-700 bg-emerald-50/90 shadow-emerald-100 text-emerald-950',
      optionCard: 'bg-white border-emerald-100 hover:border-emerald-400 hover:bg-emerald-50/40 text-slate-800',
      tableHeader: 'bg-emerald-100/70 text-emerald-950',
      tableRowEven: 'bg-emerald-50/30',
      activeTab: 'bg-emerald-700 text-white shadow-xs',
      inactiveTab: 'text-slate-600 hover:text-emerald-900',
    },
  },
  'steel-graphite': {
    id: 'steel-graphite',
    nameKey: 'theme.steelGraphite.name',
    fallbackName: 'スチール・グラファイト',
    category: 'business',
    descriptionKey: 'theme.steelGraphite.desc',
    fallbackDescription: '鋼のように堅牢でクールなグレーと論理的なスタイル',
    emoji: '📐',
    previewColors: ['#334155', '#64748b', '#f1f5f9'],
    isDark: false,
    classes: {
      pageBg: 'bg-slate-200/60 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white border-slate-300 shadow-sm text-slate-800',
      cardBorder: 'border-slate-300',
      headerCardBg: 'bg-white border-slate-300 text-slate-900',
      accentBadge: 'bg-slate-200 text-slate-800 border-slate-300',
      primaryBtn: 'bg-slate-700 hover:bg-slate-800 text-white shadow-slate-300',
      primaryText: 'text-slate-700',
      titleText: 'text-slate-900',
      bodyText: 'text-slate-700',
      mutedText: 'text-slate-400',
      divider: 'border-slate-200',
      selectedOption: 'border-slate-700 bg-slate-100 shadow-slate-200 text-slate-950',
      optionCard: 'bg-white border-slate-300 hover:border-slate-500 hover:bg-slate-50 text-slate-800',
      tableHeader: 'bg-slate-200/80 text-slate-900',
      tableRowEven: 'bg-slate-100/50',
      activeTab: 'bg-slate-700 text-white shadow-xs',
      inactiveTab: 'text-slate-600 hover:text-slate-900',
    },
  },
  'bordeaux-wine': {
    id: 'bordeaux-wine',
    nameKey: 'theme.bordeauxWine.name',
    fallbackName: 'ボルドー・ワイン',
    category: 'business',
    descriptionKey: 'theme.bordeauxWine.desc',
    fallbackDescription: '格式高い深紅ワインと上品なバーガンディのフォーマル感',
    emoji: '🍷',
    previewColors: ['#881337', '#e11d48', '#fff1f2'],
    isDark: false,
    classes: {
      pageBg: 'bg-rose-50/40 text-stone-800',
      pageContainer: 'text-stone-800',
      cardBg: 'bg-white border-rose-200 shadow-sm text-stone-800',
      cardBorder: 'border-rose-200',
      headerCardBg: 'bg-white border-rose-200 text-rose-950',
      accentBadge: 'bg-rose-100 text-rose-900 border-rose-200',
      primaryBtn: 'bg-rose-800 hover:bg-rose-900 text-white shadow-rose-200',
      primaryText: 'text-rose-800',
      titleText: 'text-rose-950',
      bodyText: 'text-stone-700',
      mutedText: 'text-stone-400',
      divider: 'border-rose-100',
      selectedOption: 'border-rose-800 bg-rose-50 shadow-rose-100 text-rose-950',
      optionCard: 'bg-white border-rose-100 hover:border-rose-400 hover:bg-rose-50/40 text-stone-800',
      tableHeader: 'bg-rose-100/60 text-rose-950',
      tableRowEven: 'bg-rose-50/30',
      activeTab: 'bg-rose-800 text-white shadow-xs',
      inactiveTab: 'text-stone-600 hover:text-rose-950',
    },
  },

  // --- 3. かっこいい / ダーク / サイバー (Cool, Dark & Cyber) ---
  'midnight-black': {
    id: 'midnight-black',
    nameKey: 'theme.midnightBlack.name',
    fallbackName: 'ミッドナイト・ダーク',
    category: 'cool',
    descriptionKey: 'theme.midnightBlack.desc',
    fallbackDescription: '漆黒の夜空とクールなブルーのシックなダークモード',
    emoji: '🌙',
    previewColors: ['#0f172a', '#38bdf8', '#020617'],
    isDark: true,
    classes: {
      pageBg: 'bg-slate-950 text-slate-100',
      pageContainer: 'text-slate-100',
      cardBg: 'bg-slate-900/90 border-slate-800 shadow-lg text-slate-100',
      cardBorder: 'border-slate-800',
      headerCardBg: 'bg-slate-900 border-slate-800 text-white',
      accentBadge: 'bg-sky-950/80 text-sky-400 border-sky-800',
      primaryBtn: 'bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold shadow-sky-950',
      primaryText: 'text-sky-400',
      titleText: 'text-white',
      bodyText: 'text-slate-200',
      mutedText: 'text-slate-400',
      divider: 'border-slate-800',
      selectedOption: 'border-sky-400 bg-sky-950/50 shadow-sky-950 text-sky-200',
      optionCard: 'bg-slate-900 border-slate-800 hover:border-sky-500/50 hover:bg-slate-800/80 text-slate-200',
      tableHeader: 'bg-slate-800 text-slate-200',
      tableRowEven: 'bg-slate-900/60',
      activeTab: 'bg-sky-500 text-slate-950 font-bold shadow-xs',
      inactiveTab: 'text-slate-400 hover:text-white',
    },
  },
  'cyber-neon': {
    id: 'cyber-neon',
    nameKey: 'theme.cyberNeon.name',
    fallbackName: 'サイバー・ネオン',
    category: 'cool',
    descriptionKey: 'theme.cyberNeon.desc',
    fallbackDescription: '暗闇に光るネオンシアンとマゼンタのサイバーパンク感',
    emoji: '⚡',
    previewColors: ['#06b6d4', '#d946ef', '#090d16'],
    isDark: true,
    classes: {
      pageBg: 'bg-[#080d1a] text-cyan-50',
      pageContainer: 'text-cyan-50',
      cardBg: 'bg-[#0d1527]/90 border-cyan-900/60 shadow-xl text-cyan-50',
      cardBorder: 'border-cyan-900/60',
      headerCardBg: 'bg-[#0d1527] border-cyan-800 text-white',
      accentBadge: 'bg-cyan-950 text-cyan-300 border-cyan-700',
      primaryBtn: 'bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-white font-black shadow-cyan-900/50',
      primaryText: 'text-cyan-400',
      titleText: 'text-white',
      bodyText: 'text-cyan-100',
      mutedText: 'text-cyan-400/60',
      divider: 'border-cyan-950',
      selectedOption: 'border-fuchsia-400 bg-fuchsia-950/40 shadow-fuchsia-950 text-fuchsia-200',
      optionCard: 'bg-[#0e172e] border-cyan-950 hover:border-cyan-500 hover:bg-[#131f3f] text-cyan-100',
      tableHeader: 'bg-[#0b1222] text-cyan-300',
      tableRowEven: 'bg-[#0e172e]/50',
      activeTab: 'bg-cyan-500 text-slate-950 font-bold shadow-xs',
      inactiveTab: 'text-cyan-300/70 hover:text-cyan-100',
    },
  },
  'deep-space': {
    id: 'deep-space',
    nameKey: 'theme.deepSpace.name',
    fallbackName: 'ディープ・スペース',
    category: 'cool',
    descriptionKey: 'theme.deepSpace.desc',
    fallbackDescription: '宇宙の深淵を思わせるダークバイオレットと神秘的な紫',
    emoji: '🌌',
    previewColors: ['#7c3aed', '#c084fc', '#0c0a1d'],
    isDark: true,
    classes: {
      pageBg: 'bg-[#0a0818] text-purple-100',
      pageContainer: 'text-purple-100',
      cardBg: 'bg-[#13102c]/90 border-purple-900/50 shadow-lg text-purple-100',
      cardBorder: 'border-purple-900/50',
      headerCardBg: 'bg-[#13102c] border-purple-800 text-white',
      accentBadge: 'bg-purple-950 text-purple-300 border-purple-800',
      primaryBtn: 'bg-purple-600 hover:bg-purple-500 text-white font-bold shadow-purple-950',
      primaryText: 'text-purple-400',
      titleText: 'text-white',
      bodyText: 'text-purple-200',
      mutedText: 'text-purple-400/60',
      divider: 'border-purple-950',
      selectedOption: 'border-purple-400 bg-purple-950/60 shadow-purple-950 text-purple-200',
      optionCard: 'bg-[#141030] border-purple-950 hover:border-purple-600 hover:bg-[#1a1540] text-purple-200',
      tableHeader: 'bg-[#100c28] text-purple-300',
      tableRowEven: 'bg-[#151035]/40',
      activeTab: 'bg-purple-600 text-white font-bold shadow-xs',
      inactiveTab: 'text-purple-300/70 hover:text-white',
    },
  },
  'sunset-glow': {
    id: 'sunset-glow',
    nameKey: 'theme.sunsetGlow.name',
    fallbackName: 'サンセット・グロー',
    category: 'cool',
    descriptionKey: 'theme.sunsetGlow.desc',
    fallbackDescription: '夕暮れのエネルギッシュなオレンジと情熱的なグラデーション',
    emoji: '🌇',
    previewColors: ['#ea580c', '#f97316', '#fff7ed'],
    isDark: false,
    classes: {
      pageBg: 'bg-gradient-to-b from-orange-50 via-amber-50/40 to-slate-50 text-stone-900',
      pageContainer: 'text-stone-900',
      cardBg: 'bg-white/95 border-orange-200 shadow-sm text-stone-900',
      cardBorder: 'border-orange-200',
      headerCardBg: 'bg-white border-orange-200 text-orange-950',
      accentBadge: 'bg-orange-100 text-orange-900 border-orange-200 font-semibold',
      primaryBtn: 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-orange-200',
      primaryText: 'text-orange-600',
      titleText: 'text-stone-950 font-black',
      bodyText: 'text-stone-800',
      mutedText: 'text-stone-400',
      divider: 'border-orange-100',
      selectedOption: 'border-orange-600 bg-orange-50 shadow-orange-100 text-orange-950',
      optionCard: 'bg-white border-stone-200 hover:border-orange-400 hover:bg-orange-50/30 text-stone-900',
      tableHeader: 'bg-orange-100/70 text-orange-950',
      tableRowEven: 'bg-orange-50/30',
      activeTab: 'bg-orange-600 text-white shadow-xs',
      inactiveTab: 'text-stone-600 hover:text-orange-900',
    },
  },

  // --- 4. かわいい / ポップ / パステル (Cute, Pop & Pastel) ---
  'sakura-pink': {
    id: 'sakura-pink',
    nameKey: 'theme.sakuraPink.name',
    fallbackName: 'さくらピンク',
    category: 'cute',
    descriptionKey: 'theme.sakuraPink.desc',
    fallbackDescription: '桜の花びらのようにふんわり華やかで優しいソフトピンク',
    emoji: '🌸',
    previewColors: ['#db2777', '#f472b6', '#fdf2f8'],
    isDark: false,
    classes: {
      pageBg: 'bg-pink-50/60 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white border-pink-200 shadow-sm text-slate-800',
      cardBorder: 'border-pink-200',
      headerCardBg: 'bg-white border-pink-200 text-pink-950',
      accentBadge: 'bg-pink-100 text-pink-800 border-pink-200 font-bold',
      primaryBtn: 'bg-pink-500 hover:bg-pink-600 text-white shadow-pink-200 font-bold',
      primaryText: 'text-pink-600',
      titleText: 'text-pink-950',
      bodyText: 'text-slate-700',
      mutedText: 'text-pink-300',
      divider: 'border-pink-100',
      selectedOption: 'border-pink-500 bg-pink-50/90 shadow-pink-100 text-pink-950',
      optionCard: 'bg-white border-pink-100 hover:border-pink-300 hover:bg-pink-50/50 text-slate-800',
      tableHeader: 'bg-pink-100/70 text-pink-950',
      tableRowEven: 'bg-pink-50/40',
      activeTab: 'bg-pink-500 text-white shadow-xs font-bold',
      inactiveTab: 'text-slate-600 hover:text-pink-700',
    },
  },
  'pastel-candy': {
    id: 'pastel-candy',
    nameKey: 'theme.pastelCandy.name',
    fallbackName: 'パステル・キャンディ',
    category: 'cute',
    descriptionKey: 'theme.pastelCandy.desc',
    fallbackDescription: 'パステルラベンダーとスカイブルーのゆめかわいいポップ調',
    emoji: '🍬',
    previewColors: ['#a855f7', '#38bdf8', '#faf5ff'],
    isDark: false,
    classes: {
      pageBg: 'bg-gradient-to-br from-purple-50/60 via-pink-50/40 to-sky-50/60 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white/90 backdrop-blur-xs border-purple-200 shadow-sm text-slate-800',
      cardBorder: 'border-purple-200',
      headerCardBg: 'bg-white border-purple-200 text-purple-950',
      accentBadge: 'bg-purple-100 text-purple-800 border-purple-200 font-bold',
      primaryBtn: 'bg-purple-500 hover:bg-purple-600 text-white shadow-purple-200 font-bold',
      primaryText: 'text-purple-600',
      titleText: 'text-purple-950',
      bodyText: 'text-slate-700',
      mutedText: 'text-purple-300',
      divider: 'border-purple-100',
      selectedOption: 'border-purple-500 bg-purple-50/80 shadow-purple-100 text-purple-950',
      optionCard: 'bg-white border-purple-100 hover:border-purple-300 hover:bg-purple-50/40 text-slate-800',
      tableHeader: 'bg-purple-100/60 text-purple-950',
      tableRowEven: 'bg-purple-50/30',
      activeTab: 'bg-purple-500 text-white shadow-xs font-bold',
      inactiveTab: 'text-slate-600 hover:text-purple-700',
    },
  },
  'sunny-citrus': {
    id: 'sunny-citrus',
    nameKey: 'theme.sunnyCitrus.name',
    fallbackName: 'サニー・シトラス',
    category: 'cute',
    descriptionKey: 'theme.sunnyCitrus.desc',
    fallbackDescription: '元気いっぱいのビタミンイエローと明るい太陽の光',
    emoji: '🍋',
    previewColors: ['#ca8a04', '#facc15', '#fefce8'],
    isDark: false,
    classes: {
      pageBg: 'bg-amber-50/60 text-stone-800',
      pageContainer: 'text-stone-800',
      cardBg: 'bg-white border-amber-300/80 shadow-sm text-stone-800',
      cardBorder: 'border-amber-300/80',
      headerCardBg: 'bg-white border-amber-300 text-amber-950',
      accentBadge: 'bg-yellow-100 text-yellow-900 border-yellow-300 font-bold',
      primaryBtn: 'bg-amber-500 hover:bg-amber-600 text-stone-950 font-bold shadow-amber-200',
      primaryText: 'text-amber-600',
      titleText: 'text-stone-900',
      bodyText: 'text-stone-700',
      mutedText: 'text-stone-400',
      divider: 'border-amber-100',
      selectedOption: 'border-amber-500 bg-amber-100/60 shadow-amber-100 text-stone-950',
      optionCard: 'bg-white border-amber-200 hover:border-amber-400 hover:bg-amber-50/60 text-stone-800',
      tableHeader: 'bg-amber-100 text-stone-900',
      tableRowEven: 'bg-amber-50/40',
      activeTab: 'bg-amber-500 text-stone-950 font-bold shadow-xs',
      inactiveTab: 'text-stone-600 hover:text-stone-900',
    },
  },
  'fresh-mint': {
    id: 'fresh-mint',
    nameKey: 'theme.freshMint.name',
    fallbackName: 'フレッシュ・ミント',
    category: 'cute',
    descriptionKey: 'theme.freshMint.desc',
    fallbackDescription: '爽快で愛らしいミントグリーンと爽やかな清涼感',
    emoji: '🌿',
    previewColors: ['#059669', '#34d399', '#ecfdf5'],
    isDark: false,
    classes: {
      pageBg: 'bg-teal-50/50 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white border-teal-200 shadow-sm text-slate-800',
      cardBorder: 'border-teal-200',
      headerCardBg: 'bg-white border-teal-200 text-teal-950',
      accentBadge: 'bg-teal-100 text-teal-900 border-teal-200 font-bold',
      primaryBtn: 'bg-teal-600 hover:bg-teal-700 text-white shadow-teal-200 font-bold',
      primaryText: 'text-teal-600',
      titleText: 'text-teal-950',
      bodyText: 'text-slate-700',
      mutedText: 'text-teal-300',
      divider: 'border-teal-100',
      selectedOption: 'border-teal-600 bg-teal-50 shadow-teal-100 text-teal-950',
      optionCard: 'bg-white border-teal-100 hover:border-teal-300 hover:bg-teal-50/40 text-slate-800',
      tableHeader: 'bg-teal-100/60 text-teal-950',
      tableRowEven: 'bg-teal-50/30',
      activeTab: 'bg-teal-600 text-white shadow-xs font-bold',
      inactiveTab: 'text-slate-600 hover:text-teal-900',
    },
  },
  'berry-smoothie': {
    id: 'berry-smoothie',
    nameKey: 'theme.berrySmoothie.name',
    fallbackName: 'ベリー・スムージー',
    category: 'cute',
    descriptionKey: 'theme.berrySmoothie.desc',
    fallbackDescription: '甘酸っぱいラズベリーとストロベリーのキュートな色彩',
    emoji: '🍓',
    previewColors: ['#be185d', '#fb7185', '#fff1f2'],
    isDark: false,
    classes: {
      pageBg: 'bg-rose-50/60 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white border-rose-200 shadow-sm text-slate-800',
      cardBorder: 'border-rose-200',
      headerCardBg: 'bg-white border-rose-200 text-rose-950',
      accentBadge: 'bg-rose-100 text-rose-800 border-rose-200 font-bold',
      primaryBtn: 'bg-rose-600 hover:bg-rose-700 text-white shadow-rose-200 font-bold',
      primaryText: 'text-rose-600',
      titleText: 'text-rose-950',
      bodyText: 'text-slate-700',
      mutedText: 'text-rose-300',
      divider: 'border-rose-100',
      selectedOption: 'border-rose-600 bg-rose-50 shadow-rose-100 text-rose-950',
      optionCard: 'bg-white border-rose-100 hover:border-rose-300 hover:bg-rose-50/40 text-slate-800',
      tableHeader: 'bg-rose-100/70 text-rose-950',
      tableRowEven: 'bg-rose-50/30',
      activeTab: 'bg-rose-600 text-white shadow-xs font-bold',
      inactiveTab: 'text-slate-600 hover:text-rose-900',
    },
  },

  // --- 5. ナチュラル / 癒やし / 和風・レトロ (Natural, Cozy & Retro) ---
  'forest-green': {
    id: 'forest-green',
    nameKey: 'theme.forestGreen.name',
    fallbackName: 'フォレスト・グリーン',
    category: 'natural',
    descriptionKey: 'theme.forestGreen.desc',
    fallbackDescription: '深い森の木々と大地を感じさせるアースカラー',
    emoji: '🌲',
    previewColors: ['#15803d', '#4ade80', '#f0fdf4'],
    isDark: false,
    classes: {
      pageBg: 'bg-emerald-50/30 text-stone-800',
      pageContainer: 'text-stone-800',
      cardBg: 'bg-white border-emerald-200 shadow-sm text-stone-800',
      cardBorder: 'border-emerald-200',
      headerCardBg: 'bg-white border-emerald-200 text-emerald-950',
      accentBadge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      primaryBtn: 'bg-emerald-700 hover:bg-emerald-800 text-white shadow-emerald-200',
      primaryText: 'text-emerald-700',
      titleText: 'text-emerald-950',
      bodyText: 'text-stone-700',
      mutedText: 'text-stone-400',
      divider: 'border-emerald-100',
      selectedOption: 'border-emerald-700 bg-emerald-50 shadow-emerald-100 text-emerald-950',
      optionCard: 'bg-white border-stone-200 hover:border-emerald-400 hover:bg-emerald-50/40 text-stone-800',
      tableHeader: 'bg-emerald-100/60 text-emerald-950',
      tableRowEven: 'bg-emerald-50/30',
      activeTab: 'bg-emerald-700 text-white shadow-xs',
      inactiveTab: 'text-stone-600 hover:text-emerald-900',
    },
  },
  'matcha-green': {
    id: 'matcha-green',
    nameKey: 'theme.matchaGreen.name',
    fallbackName: '和風・抹茶',
    category: 'natural',
    descriptionKey: 'theme.matchaGreen.desc',
    fallbackDescription: '侘び寂びを感じる上品な抹茶色と生成りの和モダン',
    emoji: '🍵',
    previewColors: ['#4d7c0f', '#a3e635', '#f7fee7'],
    isDark: false,
    classes: {
      pageBg: 'bg-lime-50/40 text-stone-800',
      pageContainer: 'text-stone-800',
      cardBg: 'bg-white border-lime-200 shadow-sm text-stone-800',
      cardBorder: 'border-lime-200',
      headerCardBg: 'bg-white border-lime-200 text-lime-950',
      accentBadge: 'bg-lime-100 text-lime-900 border-lime-300 font-medium',
      primaryBtn: 'bg-lime-800 hover:bg-lime-900 text-lime-50 shadow-lime-200',
      primaryText: 'text-lime-800',
      titleText: 'text-lime-950',
      bodyText: 'text-stone-700',
      mutedText: 'text-stone-400',
      divider: 'border-lime-100',
      selectedOption: 'border-lime-800 bg-lime-50 shadow-lime-100 text-lime-950',
      optionCard: 'bg-white border-lime-100 hover:border-lime-400 hover:bg-lime-50/40 text-stone-800',
      tableHeader: 'bg-lime-100/70 text-lime-950',
      tableRowEven: 'bg-lime-50/30',
      activeTab: 'bg-lime-800 text-lime-50 shadow-xs',
      inactiveTab: 'text-stone-600 hover:text-lime-950',
    },
  },
  'coffee-mocha': {
    id: 'coffee-mocha',
    nameKey: 'theme.coffeeMocha.name',
    fallbackName: 'カフェ・モカ',
    category: 'natural',
    descriptionKey: 'theme.coffeeMocha.desc',
    fallbackDescription: '淹れたての珈琲とキャラメルの温もりあるカフェスタイル',
    emoji: '☕',
    previewColors: ['#78350f', '#b45309', '#fef3c7'],
    isDark: false,
    classes: {
      pageBg: 'bg-amber-50/40 text-stone-800',
      pageContainer: 'text-stone-800',
      cardBg: 'bg-white border-stone-300 shadow-sm text-stone-800',
      cardBorder: 'border-stone-300',
      headerCardBg: 'bg-white border-stone-300 text-stone-900',
      accentBadge: 'bg-amber-100 text-amber-900 border-amber-300',
      primaryBtn: 'bg-amber-900 hover:bg-amber-950 text-white shadow-amber-200',
      primaryText: 'text-amber-900',
      titleText: 'text-stone-900',
      bodyText: 'text-stone-700',
      mutedText: 'text-stone-400',
      divider: 'border-stone-200',
      selectedOption: 'border-amber-900 bg-amber-50 shadow-amber-100 text-amber-950',
      optionCard: 'bg-white border-stone-200 hover:border-amber-800 hover:bg-amber-50/30 text-stone-800',
      tableHeader: 'bg-amber-100/70 text-amber-950',
      tableRowEven: 'bg-amber-50/30',
      activeTab: 'bg-amber-900 text-white shadow-xs',
      inactiveTab: 'text-stone-600 hover:text-amber-950',
    },
  },
  'retro-pop': {
    id: 'retro-pop',
    nameKey: 'theme.retroPop.name',
    fallbackName: 'レトロ・ポップ',
    category: 'natural',
    descriptionKey: 'theme.retroPop.desc',
    fallbackDescription: '70-80年代風のヴィンテージマスタード＆テラコッタ',
    emoji: '📻',
    previewColors: ['#c2410c', '#eab308', '#fffbeb'],
    isDark: false,
    classes: {
      pageBg: 'bg-orange-50/50 text-stone-800',
      pageContainer: 'text-stone-800',
      cardBg: 'bg-white border-orange-300 shadow-sm text-stone-800',
      cardBorder: 'border-orange-300',
      headerCardBg: 'bg-white border-orange-300 text-stone-900',
      accentBadge: 'bg-orange-100 text-orange-900 border-orange-300 font-bold',
      primaryBtn: 'bg-orange-700 hover:bg-orange-800 text-white shadow-orange-200 font-bold',
      primaryText: 'text-orange-700',
      titleText: 'text-stone-900',
      bodyText: 'text-stone-700',
      mutedText: 'text-stone-400',
      divider: 'border-orange-100',
      selectedOption: 'border-orange-700 bg-orange-100/60 shadow-orange-100 text-orange-950',
      optionCard: 'bg-white border-orange-200 hover:border-orange-500 hover:bg-orange-50/40 text-stone-800',
      tableHeader: 'bg-orange-100 text-orange-950 font-bold',
      tableRowEven: 'bg-orange-50/40',
      activeTab: 'bg-orange-700 text-white shadow-xs font-bold',
      inactiveTab: 'text-stone-600 hover:text-orange-950',
    },
  },
  'lavender-garden': {
    id: 'lavender-garden',
    nameKey: 'theme.lavenderGarden.name',
    fallbackName: 'ラベンダー・ガーデン',
    category: 'natural',
    descriptionKey: 'theme.lavenderGarden.desc',
    fallbackDescription: '心やすらぐハーブの香りと優しいラベンダーパープル',
    emoji: '🪻',
    previewColors: ['#7e22ce', '#c084fc', '#faf5ff'],
    isDark: false,
    classes: {
      pageBg: 'bg-purple-50/50 text-slate-800',
      pageContainer: 'text-slate-800',
      cardBg: 'bg-white border-purple-200 shadow-sm text-slate-800',
      cardBorder: 'border-purple-200',
      headerCardBg: 'bg-white border-purple-200 text-purple-950',
      accentBadge: 'bg-purple-100 text-purple-800 border-purple-200',
      primaryBtn: 'bg-purple-700 hover:bg-purple-800 text-white shadow-purple-200',
      primaryText: 'text-purple-700',
      titleText: 'text-purple-950',
      bodyText: 'text-slate-700',
      mutedText: 'text-purple-300',
      divider: 'border-purple-100',
      selectedOption: 'border-purple-700 bg-purple-50 shadow-purple-100 text-purple-950',
      optionCard: 'bg-white border-purple-100 hover:border-purple-400 hover:bg-purple-50/40 text-slate-800',
      tableHeader: 'bg-purple-100/60 text-purple-950',
      tableRowEven: 'bg-purple-50/30',
      activeTab: 'bg-purple-700 text-white shadow-xs',
      inactiveTab: 'text-slate-600 hover:text-purple-900',
    },
  },
};

/**
 * Returns the theme definition for a given theme ID.
 * Defaults to 'default' (Classic Indigo) if themeId is undefined or not found.
 */
export function getTheme(themeId?: string): ThemeDefinition {
  if (!themeId || !THEMES[themeId]) {
    return THEMES['default'];
  }
  return THEMES[themeId];
}

/**
 * Returns all themes as an array.
 */
export function getAllThemes(): ThemeDefinition[] {
  return Object.values(THEMES);
}

/**
 * Returns themes filtered by category.
 */
export function getThemesByCategory(category: ThemeCategory): ThemeDefinition[] {
  if (category === 'all') {
    return getAllThemes();
  }
  return getAllThemes().filter(t => t.category === category);
}
